import { useEffect, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { organSchema } from "../../yup";
import Input from "../../components/UserInput/Input";
import { useUpdateOrganDetailsMutation } from "../../services/api";
import style from "./organ.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import { getErrorMessage } from "../../utils/common";

type FormState = typeof organSchema.__outputType;

type EditOrganNavState = {
  organ?: Organ;
};

/**
 * Edit Organ page for updating an existing organ.
 *
 * Notes:
 * - The row being edited is handed over in router state by the Organs list, so
 *   this screen does not re-fetch it. Browsers persist history.state, so a
 *   reload still works — but a new tab or a shared link carries no state, which
 *   is why the no-state fallback below sends the user back to the list.
 * - Validation is enforced via `organSchema`.
 */
const EditOrgan = () => {
  const [updateOrgan] = useUpdateOrganDetailsMutation();
  const { id } = useParams();
  const { state } = useLocation();
  const { organ: organDetails } = (state ?? {}) as EditOrganNavState;
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const navigation = useNavigate();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [isSavingOrgan, setIsSavingOrgan] = useState(false);

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(organSchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FormState>({
    resolver: yupResolver(organSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (organDetails) {
      // Reset from the handed-over record to avoid stale edits when navigating
      // between rows without a remount.
      reset({
        name: organDetails.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organDetails]);

  /**
   * Submits the updated organ payload.
   *
   * Args:
   *   values (FormState): Form values for the organ update.
   *
   * Returns:
   *   Promise<void>
   *
   * Raises:
   *   Error: Propagated when the update mutation fails.
   *
   * Notes:
   * - Uses route `id`; blocks submit when `id` is invalid.
   */
  const onSubmit = async (values: FormState) => {
    try {
      // Block updates if route id is missing/invalid to avoid unintended mutations.
      const parsedId = parseInt(id ?? "", 10);
      if (Number.isNaN(parsedId)) {
        setMessage({
          type: "error",
          text: "Unable to update organ. Please reload and try again.",
        });
        return;
      }
      setIsSavingOrgan(true);
      const { name } = values;
      const payload: EditOrganPayload = {
        id: parsedId,
        name: name,
      };

      await updateOrgan(payload).unwrap();
      setIsSavingOrgan(false);
      setMessage({
        type: "success",
        text: "Organ updated successfully.",
      });
      navigationTimeoutRef.current = setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Please enter valid details"),
      });
      setIsSavingOrgan(false);
    }
  };
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  if (!organDetails) {
    return (
      <Box className={style.container}>
        <Typography className={style.detailHeading} sx={{ marginTop: "10px" }}>
          This organ is no longer available
        </Typography>
        <Typography sx={{ color: colors.grey, marginTop: "10px" }}>
          Organ details are carried over from the list screen, so this page
          cannot be opened directly or shared as a link. Open it again from
          Organs.
        </Typography>
        <Box className={style.buttons}>
          <Button
            sx={{ width: "200px", height: "55px" }}
            variant="contained"
            onClick={() => navigation("/organs")}
            label={"Back to Organs"}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Box>
          <Typography
            className={style.detailHeading}
            sx={{ marginTop: "10px" }}
          >
            Organ Details
          </Typography>
          <Box className={style.flexTop}>
            <Box className={style.row}>
              <Input
                placeholder="e.g Liver"
                label="Name"
                required
                register={register}
                name="name"
                errors={errors}
              />
            </Box>
          </Box>
          {message && (
            <Box mt={2}>
              <LabelBox
                boxProps={{ height: 50, display: "flex", alignItems: "center" }}
                data={message}
                onClose={() => setMessage(null)}
              />
            </Box>
          )}
          <Box className={style.buttons}>
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: "transparent" },
              }}
              variant="outlined"
              onClick={() => navigation(-1)}
              label={"Back"}
            />
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: colors.primary },
              }}
              variant="contained"
              type="submit"
              label={"Save"}
              loading={isSavingOrgan}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default EditOrgan;
